import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import * as Highcharts from 'highcharts';
import type { DqaFarmHistoryEntry, DqaHistoryEntry } from '@/types/DataQualityAssessment';
import type { DataQualityCategory } from '@/types';
import { QUALITY_CATEGORY_STYLES } from '@/components/common/dataQuality/qualityCategoryStyles';


export default /*@__PURE__*/_defineComponent({
  __name: 'DataQualityHistoryChart',
  props: {
    history: {},
    showFarms: { type: Boolean },
    hiddenFarmIds: {},
    selectedDateStr: {},
    categories: {},
    directoryName: {}
  },
  emits: ["dateSelected"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const chartEl = ref<HTMLElement | null>(null);
let chart: Highcharts.Chart | null = null;

function categoryNameByKey(key: string): string {
    return props.categories.find((c) => c.key === key)?.name ?? key;
}

function categoryChartColor(key: string): string | undefined {
    return QUALITY_CATEGORY_STYLES[key]?.chart;
}

function categoryById(id: number | undefined): DataQualityCategory | undefined {
    return id === undefined ? undefined : props.categories.find((c) => c.id === id);
}

function escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// The awarded category is not a pure function of the score - a directory or farm only
// reaches the top category when every underlying assessment does - so the marker and the
// tooltip carry the category the server assigned, not the y-axis band the point falls in.
// While the categories are still loading, or for a category this client has no style for,
// the marker keeps the series colour so it makes no claim the tooltip cannot back up.
function selectedMarker(categoryId: number): Highcharts.PointMarkerOptionsObject {
    const fillColor = categoryChartColor(categoryById(categoryId)?.key ?? '');

    return { enabled: true, radius: 5, ...(fillColor ? { fillColor } : {}), lineColor: '#ffffff', lineWidth: 2 };
}

function buildSeries(): Highcharts.SeriesOptionsType[] {
    if (!props.showFarms) {
        return [
            {
                type: 'line',
                name: props.directoryName || 'Directory',
                color: '#3b82f6',
                connectNulls: false,
                data: props.history.map((e) => ({
                    x: new Date(e.date).getTime(),
                    y: e.score ?? null,
                    custom: { categoryId: e.quality_assessment_id },
                    marker: e.date === props.selectedDateStr ? selectedMarker(e.quality_assessment_id) : { enabled: false },
                    events: { click: () => emit('dateSelected', e.date) },
                })),
            },
        ];
    }

    const farmMap = new Map<number, { name: string; entries: Map<string, DqaFarmHistoryEntry> }>();
    for (const entry of props.history) {
        for (const farm of entry.farm_summaries) {
            if (!farmMap.has(farm.farm_id)) {
                farmMap.set(farm.farm_id, { name: farm.farm_name, entries: new Map() });
            }
            farmMap.get(farm.farm_id)!.entries.set(entry.date, farm);
        }
    }

    return Array.from(farmMap.entries()).map(([farmId, { name, entries }]) => ({
        type: 'line' as const,
        id: `farm-${farmId}`,
        name,
        visible: !props.hiddenFarmIds.includes(farmId),
        connectNulls: false,
        data: props.history.map((e) => {
            const farm = entries.get(e.date);
            return {
                x: new Date(e.date).getTime(),
                y: farm?.score ?? null,
                custom: { categoryId: farm?.quality_assessment_id },
                marker: e.date === props.selectedDateStr && farm ? selectedMarker(farm.quality_assessment_id) : { enabled: false },
                events: { click: () => emit('dateSelected', e.date) },
            };
        }),
    }));
}

function buildOptions(): Highcharts.Options {
    const selectedTs = props.selectedDateStr ? new Date(props.selectedDateStr).getTime() : null;

    return {
        chart: { type: 'line', animation: false },
        title: { text: undefined },
        credits: { enabled: false },
        exporting: { enabled: false },
        xAxis: {
            type: 'datetime',
            crosshair: false,
            tickPositions: props.history.map((e) => new Date(e.date).getTime()),
            labels: { format: '{value:%Z}' },
            plotLines: selectedTs
                ? [
                      {
                          color: '#6b7280',
                          width: 1.5,
                          value: selectedTs,
                          dashStyle: 'LongDash' as Highcharts.DashStyleValue,
                          zIndex: 1,
                      },
                  ]
                : [],
        },
        yAxis: {
            min: 0,
            max: 11,
            endOnTick: false,
            title: { text: null },
            tickPositions: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            plotBands: [
                {
                    from: 0,
                    to: 3,
                    color: 'rgba(239, 68, 68, 0.15)',
                    zIndex: 0,
                    label: { text: categoryNameByKey('Inferior'), style: { color: categoryChartColor('Inferior'), fontSize: '11px', fontWeight: '600' } },
                },
                {
                    from: 3,
                    to: 9,
                    color: 'rgba(234, 179, 8, 0.15)',
                    zIndex: 0,
                    label: { text: categoryNameByKey('Uncertain'), style: { color: categoryChartColor('Uncertain'), fontSize: '11px', fontWeight: '600' } },
                },
                {
                    from: 9,
                    to: 11,
                    color: 'rgba(34, 197, 94, 0.15)',
                    zIndex: 0,
                    label: { text: categoryNameByKey('Excellent'), style: { color: categoryChartColor('Excellent'), fontSize: '11px', fontWeight: '600' } },
                },
            ],
        },
        tooltip: {
            shared: true,
            formatter(): string | false {
                const pts = (this.points ?? []).filter((p) => p.y !== null && p.y !== undefined);
                if (!pts.length) return false;
                const header = Highcharts.dateFormat('%e %B %Y', this.x as number);
                const rows = pts
                    .map((p) => {
                        // Highcharts types `custom` as an untyped bag; the point builders above put
                        // the category id there and nothing else writes to it.
                        const category = categoryById(p.point.options.custom?.categoryId as number | undefined);
                        const color = category ? categoryChartColor(category.key) : undefined;
                        const suffix = category ? ` <span style="color:${color ?? 'inherit'}">${escapeHtml(category.name)}</span>` : '';

                        // The series name is a farm name the customer typed, so it is escaped before
                        // it reaches this HTML tooltip.
                        return `<span style="color:${p.color}">●</span> ${escapeHtml(p.series.name)}: <b>${(p.y as number).toFixed(1)}</b>${suffix}`;
                    })
                    .join('<br/>');
                return `${header}<br/>${rows}`;
            },
        },
        plotOptions: {
            series: {
                cursor: 'pointer',
                marker: { enabled: false },
            },
        },
        series: buildSeries(),
    };
}

function updateSelectedDate(newDateStr: string, oldDateStr: string) {
    if (!chart) return;

    const newTs = newDateStr ? new Date(newDateStr).getTime() : null;
    const oldTs = oldDateStr ? new Date(oldDateStr).getTime() : null;

    chart.xAxis[0].update(
        {
            plotLines: newTs ? [{ color: '#6b7280', width: 1.5, value: newTs, dashStyle: 'LongDash' as Highcharts.DashStyleValue, zIndex: 1 }] : [],
        },
        false
    );

    for (const series of chart.series) {
        for (const point of series.points) {
            const x = point.x as number;
            if (x === newTs) {
                const categoryId = point.options.custom?.categoryId as number | undefined;
                point.update({ marker: categoryId === undefined ? { enabled: false } : selectedMarker(categoryId) }, false);
            } else if (x === oldTs) {
                point.update({ marker: { enabled: false } }, false);
            }
        }
    }

    chart.redraw();
}

onMounted(() => {
    if (chartEl.value) {
        chart = Highcharts.chart(chartEl.value, buildOptions());
    }
});

watch([() => props.history, () => props.showFarms, () => props.categories], () => {
    if (!chartEl.value) return;
    chart?.destroy();
    chart = Highcharts.chart(chartEl.value, buildOptions());
});

watch(
    () => props.hiddenFarmIds,
    (newHiddenIds) => {
        if (!chart || !props.showFarms) return;
        for (const series of chart.series) {
            const id = series.options.id;
            if (!id?.startsWith('farm-')) continue;
            const farmId = parseInt(id.slice(5), 10);
            series.setVisible(!newHiddenIds.includes(farmId), false);
        }
        chart.redraw();
    }
);

watch(
    () => props.selectedDateStr,
    (newVal, oldVal) => updateSelectedDate(newVal, oldVal ?? '')
);

onBeforeUnmount(() => {
    chart?.destroy();
    chart = null;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "chartEl",
    ref: chartEl,
    class: "w-full h-64"
  }, null, 512))
}
}

})